/**
 * Decides whether a visitor may enter the authenticated part of the dashboard, and if not, what
 * the login route needs to know in order to send them back afterwards.
 *
 * It exists apart from `routes/_authenticated.tsx` for one reason: the decision was expressed as
 * control flow inside a TanStack Router `beforeLoad` — a `throw redirect(...)` — so observing it
 * meant constructing a router. The phase-14 sweep deleted the entire `if (!token)` block and all
 * 106 tests passed. This is the highest-leverage line in the web app; it now has a home where it
 * can be asked questions directly.
 *
 * It deliberately knows nothing about: the router, `redirect()`, how a redirect is thrown, React,
 * how the token was obtained, or whether Supabase was even reachable. It is handed a token and a
 * location and returns a decision. `routes/_authenticated.tsx` keeps the throwing — the decision
 * moved, the redirect stayed.
 *
 * Failing open is the whole risk. A visitor with no session then renders the full authenticated
 * shell: every hook fires, every panel fills with error toasts and empty states, and because
 * `routes/login.tsx` runs the inverse check, there is no way back to a login form. No customer
 * data leaks — the API still answers 401 to a request with no bearer token — but the account is
 * unusable until storage is cleared. A logged-out user opening a `takelead.app/billing` bookmark
 * lands there permanently.
 */

/** What `/login` is told, so it can return the user to where they were headed. */
export interface LoginSearch {
  extensionAuth?: string;
  redirect?: string;
  from?: string;
}

export type AuthGuardDecision =
  | { kind: "allow" }
  | { kind: "redirect"; to: "/login"; search: LoginSearch };

/** The parts of `window.location` this needs; `null` means there is no browser location at all. */
export interface GuardLocation {
  search: string;
  pathname: string;
}

export function evaluateAuthGuard(
  token: string | null,
  location: GuardLocation | null,
): AuthGuardDecision {
  /**
   * Matches every consumer's truthiness check rather than testing `!== null`, so a blank string
   * can never be the difference between allowed and redirected. `accessTokenFrom` already
   * normalises blanks away; this is the second half of the same rule, stated where it is acted on.
   */
  if (typeof token === "string" && token.trim()) return { kind: "allow" };

  const params = location ? new URLSearchParams(location.search) : null;
  const extensionAuth = params?.get("extensionAuth");
  const redirectParam = params?.get("redirect");

  /**
   * The extension handoff is carried only as a PAIR. One half without the other is not a partial
   * handoff to be salvaged — `__root.tsx` forwards the token only when `extensionAuth === "1"`
   * and `redirect` points at a `chrome-extension://` URL, so a lone `redirect` would be an open
   * parameter that survives a login round-trip for nothing.
   */
  const search: LoginSearch =
    extensionAuth && redirectParam
      ? { extensionAuth, redirect: redirectParam }
      : {};

  /** `from: "/login"` would make the login page bounce to itself once the session appears. */
  const fromPath = location?.pathname;
  if (fromPath && fromPath !== "/login") search.from = fromPath;

  return { kind: "redirect", to: "/login", search };
}
